import React from 'react';
import { Tag } from '@jem-open/jem-ui';
import {
  computeInvoiceGroups,
  invoiceGroupLabel,
  RECON_PRODUCTS,
  ReconGroup,
  Schedule,
  useMultiProductRecon,
} from '../MultiProductReconContext';

const WEEKDAY_LABELS: Record<string, string> = {
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
};

const NTH_LABELS: Record<string, string> = {
  '1': '1st',
  '2': '2nd',
  '3': '3rd',
  '4': '4th',
  '-1': 'last',
};

const formatStartDate = (date: Date | null): string => {
  if (!date) return '-';
  return date.toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatRecurrence = (schedule: Schedule): string => {
  if (schedule.repeatFrequency === 'weekly') {
    const every =
      schedule.interval && schedule.interval > 1
        ? `every ${schedule.interval} weeks`
        : 'weekly';
    const day = schedule.dayOfWeek ? WEEKDAY_LABELS[schedule.dayOfWeek] : null;
    return day ? `${every} on ${day}` : every;
  }
  if (schedule.repeatFrequency === 'monthly') {
    if (
      schedule.monthlyType === 'byDateInMonth' &&
      schedule.dateInMonth !== null
    ) {
      return `monthly on day ${schedule.dateInMonth}`;
    }
    if (schedule.monthlyType === 'nthDayInMonth') {
      const nth =
        schedule.nthDayInMonth !== null
          ? NTH_LABELS[String(schedule.nthDayInMonth)]
          : null;
      const day = schedule.posDayInMonth
        ? WEEKDAY_LABELS[schedule.posDayInMonth]
        : null;
      if (nth && day) return `monthly on the ${nth} ${day}`;
    }
    return 'monthly';
  }
  return '-';
};

const productLabels = (products: ReconGroup['products']): string =>
  products
    .map((p) => RECON_PRODUCTS.find((rp) => rp.value === p)?.label ?? p)
    .join(' + ');

const STRATEGY_LABEL: Record<string, string> = {
  separate: 'Separate invoice per recon',
  perProduct: 'Separate invoice per product',
  combined: 'One combined invoice',
  custom: 'Custom grouping',
};

const ConfirmStep = () => {
  const { state } = useMultiProductRecon();
  const groups = computeInvoiceGroups(
    state.recons,
    state.invoiceStrategy,
    state.customInvoiceGroupByProduct,
  );

  return (
    <div className="space-y-5">
      <section className="space-y-3">
        <h3 className="text-[18px] font-semibold text-[--greyscale-text-title]">
          Calendar event
        </h3>
        <div className="space-y-2 rounded-md border bg-white p-4">
          <div className="flex justify-between gap-3 text-sm">
            <dt className="text-muted-foreground">Title</dt>
            <dd className="text-right font-medium">{state.title || '-'}</dd>
          </div>
          <div className="flex justify-between gap-3 text-sm">
            <dt className="text-muted-foreground">Description</dt>
            <dd className="text-right font-medium">
              {state.description || '-'}
            </dd>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-[18px] font-semibold text-[--greyscale-text-title]">
          Schedule
        </h3>
        <div className="space-y-2 rounded-md border bg-white p-4">
          <div className="flex justify-between gap-3 text-sm">
            <dt className="text-muted-foreground">Repeat</dt>
            <dd className="text-right font-medium">
              {formatRecurrence(state.schedule)}
            </dd>
          </div>
          <div className="flex justify-between gap-3 text-sm">
            <dt className="text-muted-foreground">Start</dt>
            <dd className="text-right font-medium">
              {formatStartDate(state.schedule.startDate)}
            </dd>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-[18px] font-semibold text-[--greyscale-text-title]">
          Recons summary
        </h3>
        <div className="space-y-3">
          {state.recons.map((recon, index) => (
            <div
              key={recon.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-white p-4"
            >
              <h4 className="font-semibold text-[--greyscale-text-title]">
                Recon {index + 1} -{' '}
                {productLabels(recon.products) || 'no products'}
              </h4>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-[18px] font-semibold text-[--greyscale-text-title]">
            Invoicing
          </h3>
          <Tag variant="outline-navy">
            {STRATEGY_LABEL[state.invoiceStrategy] ?? state.invoiceStrategy}
          </Tag>
        </div>
        {groups.length > 0 && (
          <div className="space-y-2 rounded-md border bg-white p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {groups.length === 1 ? '1 invoice' : `${groups.length} invoices`}{' '}
              will be generated
            </p>
            <ul className="space-y-1">
              {groups.map((g) => (
                <li
                  key={g.groupNumber}
                  className="flex flex-wrap items-center gap-2 text-sm"
                >
                  <Tag variant="outline-navy">Invoice {g.groupNumber}</Tag>
                  <span className="text-[--greyscale-text-body]">
                    {invoiceGroupLabel(g, state.recons, state.invoiceStrategy)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="text-[18px] font-semibold text-[--greyscale-text-title]">
          Email recipients
        </h3>
        <div className="rounded-md border bg-white p-3 text-sm text-[--greyscale-text-body]">
          {state.attendees.length > 0 ? state.attendees.join(', ') : '-'}
        </div>
      </section>
    </div>
  );
};

export default ConfirmStep;
